/**
 * شاشة «سجل الطلاب» — `/students` (design/StudentsList.dc.html).
 *
 * الشاشة دي كمان بوابة سجل الطالب: `/students/{id}` بيطابق نفس المسار في
 * `ROUTES` (المطابقة بالبادئة)، فالشاشة بتحوّل لـ`StudentRecord` بنفسها.
 *
 * ⚠️ الحاجات اللي الشاشة **بتقولها بصوت** بدل ما تخبّيها:
 *   • «المعلم الحالي» مش في رد القايمة — رد `GET /students` مفيهوش
 *     الاشتراكات. عمود كله «—» كان هيوحي إن الطلاب بلا معلمين، فبدّلناه
 *     بفلتر بالمعلم + سطر بيقول الحقيقة. (`TEACHER_COLUMN_NOTE_AR`)
 *   • عدّاد الشريحة اللي فشل بيتعرض **بلا رقم** — مش صفر.
 *   • مجموع الشرايح **مش** بيساوي «الكل» عمدًا (فيه حالات مش معروضة،
 *     و«محتاج استكمال» بتتقاطع مع الباقي) — فالشاشة ما بتدّعيش تطابق.
 *
 * ⛔ التصدير لسه ما اتبناش — الزرار مقفول ومكتوب جنبه السبب، مش زرار
 *    بيضغط وما بيعملش حاجة.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoResultsState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { Avatar, Dash, DateAr, NeedsReviewMark, SinceAr, VipMark } from "@/components/RegistryBits";
import { Ltr, fmtNum } from "@/lib/format";
import { errorMessageAr, OfflineError } from "@/api/client";
import { useRouter } from "@/lib/router";
import {
  STUDENT_SEGMENTS,
  TEACHER_COLUMN_NOTE_AR,
  fetchStudentCounts,
  fetchStudents,
  fetchTeacherOptions,
  languageAr,
  latinDigits,
  studentTone,
  type Page,
  type StudentRow,
  type StudentSegment,
  type TeacherRow,
  type ReturnPlan,
} from "@/api/registry";
import { StudentRecord } from "@/screens/StudentRecord";
import "@/styles/registry.css";

/** الصفحة 8 صفوف — زي «عرض 1–8 من 199» في التصميم */
const PER_PAGE = 8;
/** الباك إند بيرفض بحث بأقل من حرفين (`SEARCH_TOO_SHORT`) — بنمنعه قبل ما يطلع */
const MIN_SEARCH = 2;
const SEARCH_DEBOUNCE_MS = 350;

type PageState =
  | { kind: "loading" }
  | { kind: "ready"; value: Page<StudentRow> }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

/** `/students/9f2…` ⇒ `9f2…` · `/students` ⇒ `null` */
export function studentIdFromPath(path: string): string | null {
  const m = /^\/students\/([^/?#]+)/.exec(path);
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

export function Students() {
  const { path } = useRouter();
  const studentId = studentIdFromPath(path);
  // نفس المسار في `ROUTES` بيخدم القايمة والسجل — التحويل هنا مش في الراوتر
  if (studentId) return <StudentRecord studentId={studentId} />;
  return <StudentsList />;
}

function StudentsList() {
  const { navigate } = useRouter();

  const [segment, setSegment] = useState<StudentSegment>("all");
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [counts, setCounts] = useState<Record<StudentSegment, number | null> | null>(null);
  const [teachers, setTeachers] = useState<TeacherRow[]>([]);
  const [teachersError, setTeachersError] = useState<string | null>(null);

  /** البحث بيتأخّر شوية عشان ما نضربش السيستم على كل حرف */
  const debounce = useRef<number | null>(null);
  useEffect(() => {
    const trimmed = searchInput.trim();
    const next = trimmed.length >= MIN_SEARCH ? trimmed : "";
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => {
      setQuery((cur) => (cur === next ? cur : next));
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounce.current) window.clearTimeout(debounce.current);
    };
  }, [searchInput]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const value = await fetchStudents({
        segment,
        page,
        perPage: PER_PAGE,
        q: query || null,
        teacherId: teacherId || null,
      });
      setState({ kind: "ready", value });
    } catch (err) {
      // ⛔ ممنوع الفشل الصامت
      console.error("[students] تحميل سجل الطلاب فشل", err);
      setState(
        err instanceof OfflineError
          ? { kind: "offline" }
          : { kind: "error", messageAr: errorMessageAr(err) },
      );
    }
  }, [segment, page, query, teacherId]);

  useEffect(() => {
    void load();
  }, [load]);

  // العدّادات بتتبع البحث والفلتر بالمعلم — مش الشريحة النشطة
  useEffect(() => {
    let alive = true;
    setCounts(null);
    void fetchStudentCounts({ q: query || null, teacherId: teacherId || null }).then((c) => {
      if (alive) setCounts(c);
    });
    return () => {
      alive = false;
    };
  }, [query, teacherId]);

  useEffect(() => {
    let alive = true;
    fetchTeacherOptions().then(
      (rows) => {
        if (alive) setTeachers(rows);
      },
      (err: unknown) => {
        console.error("[students] قايمة المعلمين مش متاحة — الفلتر بالمعلم هيتقفل", err);
        if (alive) setTeachersError(errorMessageAr(err));
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  const data = state.kind === "ready" ? state.value : null;
  const rows = data?.rows ?? [];
  const active = STUDENT_SEGMENTS.find((s) => s.key === segment) ?? STUDENT_SEGMENTS[0];
  // 🔑 «متوقف» لوحدها مش معلومة قابلة للتصرّف — المشرفة بتبص على ٩٧ طالب
  //    وبتسأل «مين هيرجع إمتى؟»، والإجابة كانت متخبّية جوّه تبويب الإجازات
  //    لكل طالب على حدة (يعني ٩٧ فتحة سجل).
  const showReturn = segment === "paused" || segment === "suspended_by_academy";
  const total = data?.total ?? 0;
  // ⚠️ الترقيم من رد السيستم مش من الحالة المحلية — لو السيستم صحّح الصفحة،
  //    «عرض 1–8 من 199» لازم تقول الحقيقة.
  const shownPage = data?.page ?? page;
  const shownPerPage = data?.perPage ?? PER_PAGE;
  const firstIndex = total === 0 ? 0 : (shownPage - 1) * shownPerPage + 1;
  const lastIndex = total === 0 ? 0 : firstIndex + rows.length - 1;
  const totalPages = data?.totalPages ?? 1;

  const teacherName = useMemo(
    () => teachers.find((t) => t.id === teacherId)?.name_ar ?? null,
    [teachers, teacherId],
  );

  const crumb = counts?.all !== null && counts?.all !== undefined ? (
    <>
      <span className="num">{fmtNum(counts.all)}</span> طالب في السجل
      {counts.needs_review !== null && counts.needs_review !== undefined ? (
        <>
          {" · "}
          <span className="num">{fmtNum(counts.needs_review)}</span> محتاج استكمال
        </>
      ) : null}
    </>
  ) : null;

  return (
    <Shell
      title="سجل الطلاب"
      {...(crumb ? { crumb } : {})}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: "ابحث باسم الطالب أو كوده…",
      }}
    >
      {/* ═══ (1) الشرايح + التصدير ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {STUDENT_SEGMENTS.map((s) => {
          const n = counts?.[s.key] ?? null;
          return (
            <button
              key={s.key}
              type="button"
              className={`filt${segment === s.key ? " on" : ""}`}
              aria-pressed={segment === s.key}
              onClick={() => {
                setSegment(s.key);
                setPage(1);
              }}
            >
              {s.chip}
              {n === null ? null : (
                <>
                  {" "}
                  <span className="num">{fmtNum(n)}</span>
                </>
              )}
            </button>
          );
        })}
        <div
          style={{ marginRight: "auto", display: "flex", alignItems: "center", gap: 10 }}
        >
          <span className="t-meta">التصدير لسه بيتجهّز</span>
          <button type="button" className="btn ghost is-off" disabled>
            تصدير
          </button>
        </div>
      </div>

      {/* ═══ (2) الفلتر بالمعلم + الحقيقة عن عمود «المعلم الحالي» ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {teachersError ? (
          <span className="t-meta">قايمة المعلمين مش متاحة — {teachersError}</span>
        ) : (
          <select
            className="reg-select"
            style={{ flex: "0 1 260px" }}
            aria-label="فلترة بالمعلم"
            value={teacherId}
            onChange={(e) => {
              setTeacherId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">كل المعلمين</option>
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name_ar}
              </option>
            ))}
          </select>
        )}
        <span className="t-meta">{TEACHER_COLUMN_NOTE_AR}</span>
      </div>

      {/* ═══ (3) الجدول — العنوان تابع للشريحة النشطة ═══ */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div className="sec-h" style={{ padding: "14px 16px 10px" }}>
          <span className="sec-title">{active?.title}</span>
          <span className="t-meta">
            مرتّب بالاسم
            {teacherName ? ` · طلاب ${teacherName}` : ""}
            {query ? ` · بحث عن «${query}»` : ""}
          </span>
        </div>

        {state.kind === "loading" ? <LoadingState rows={5} /> : null}
        {state.kind === "offline" ? <OfflineState onRetry={() => void load()} /> : null}
        {state.kind === "error" ? (
          <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
        ) : null}

        {/* ⛔ حالات فاضية حقيقية — مش شاشة بيضا */}
        {state.kind === "ready" && rows.length === 0 && query ? (
          <NoResultsState query={query} onClear={() => setSearchInput("")} />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query && segment !== "all" ? (
          <EmptyState
            title={`مفيش طلاب في «${active?.chip}»`}
            hint="جرّب شريحة تانية أو شوف «الكل»"
            action={{ label: "عرض الكل", onClick: () => setSegment("all") }}
          />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query && segment === "all" && teacherId ? (
          <EmptyState
            title={`مفيش طلاب على ${teacherName ?? "المعلم ده"}`}
            hint="شيل الفلتر عشان تشوف السجل كله"
            action={{ label: "كل المعلمين", onClick: () => setTeacherId("") }}
          />
        ) : null}
        {state.kind === "ready" &&
        rows.length === 0 &&
        !query &&
        segment === "all" &&
        !teacherId ? (
          <EmptyState
            title="لسه مفيش طلاب في السجل"
            hint="الطالب بيدخل السجل أول ما طلب أسرته يتحوّل لتسجيل"
          />
        ) : null}

        {rows.length > 0 ? (
          <div className="reg-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>الطالب</th>
                  <th>الأسرة</th>
                  <th>الدولة</th>
                  <th>اللغة</th>
                  <th>آخر تواصل</th>
                  {/* 🔑 بيظهر مع شريحة المتوقفين بس — عمود فاضي على النشط
                      بيضيّع عرض ومابيقولش حاجة. */}
                  {showReturn ? <th>موعد الرجوع</th> : null}
                  <th>الحالة</th>
                  <th>إجراء</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="row-link">
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar name={row.name_ar} size={28} />
                        <div style={{ minWidth: 0 }}>
                          {/* 🔑 **الاسم هو نفسه الرابط.**
                              المقاس على 390: الجدول عرضه 684 في حاوية 282،
                              وزرار «فتح السجل» في آخر عمود `left = -366` —
                              يعني الفعل الأساسي على بُعد ٢.٤ عرض شاشة سحب
                              أفقي. والصف **مش** بديل: `.row-link` مجرد لون
                              تحويم، ومفيش `<tr>` واحد في السيستم عنده
                              `onClick`.
                              ⛔ وممنوع `onClick` على الـ`<tr>` نفسه — الصف
                              فيه أزرار جوّاه وده بيعمل نقر مزدوج. */}
                          <button
                            type="button"
                            className="link t-body clip"
                            style={{ fontWeight: 600, color: "var(--ink)", textAlign: "start", maxWidth: "100%" }}
                            onClick={() => navigate(`/students/${row.id}`)}
                          >
                            {row.name_ar || <span className="missing">بلا اسم مسجّل</span>}
                          </button>
                          <div
                            className="t-meta"
                            style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
                          >
                            {row.age_years === null ? (
                              <span className="missing">السن مش مسجّل</span>
                            ) : (
                              <span>
                                <span className="num">{fmtNum(row.age_years)}</span> سنة
                              </span>
                            )}
                            {row.student_code ? <Ltr>{row.student_code}</Ltr> : null}
                            {/* التصنيف جنب وسم الاستكمال مش في عمود لوحده —
                                عمود بيفضل فاضي لمعظم الصفوف بياكل عرضًا بلا فايدة */}
                            {row.family?.is_vip ? (
                              <VipMark tagsAr={[{ key: "vip", label_ar: "VIP" }]} />
                            ) : null}
                            <NeedsReviewMark q={row.data_quality} />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="t-desc">
                      {row.family ? (
                        row.family.name_ar || <span className="missing">أسرة بلا اسم</span>
                      ) : (
                        // الباك إند بيسجّل ERROR على الربط الميت — والشاشة بتقوله كمان
                        <span className="missing">الأسرة مش موجودة في السجل</span>
                      )}
                    </td>
                    <td className="t-desc">
                      {row.family?.country ? <Ltr>{row.family.country}</Ltr> : <Dash />}
                    </td>
                    <td className="t-desc">
                      {languageAr(row.family?.primary_language) ?? <Dash />}
                    </td>
                    <td className="t-desc">
                      <SinceAr at={row.last_contact_at} />
                    </td>
                    {showReturn ? (
                      <td className="t-desc">
                        <ReturnCell plan={row.return_plan} />
                      </td>
                    ) : null}
                    <td>
                      {row.status_label_ar ? (
                        <span className={`chip ${studentTone(row.status)}`}>
                          <span className="bullet" />
                          {latinDigits(row.status_label_ar)}
                        </span>
                      ) : (
                        <Dash />
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn sm ghost"
                        onClick={() => navigate(`/students/${row.id}`)}
                      >
                        فتح السجل
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {/* ═══ (4) الترقيم ═══ */}
      {rows.length > 0 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span className="t-meta">
            عرض <span className="num">{fmtNum(firstIndex)}</span>–
            <span className="num">{fmtNum(lastIndex)}</span> من{" "}
            <span className="num">{fmtNum(total)}</span>
          </span>
          <button
            type="button"
            className="btn sm ghost"
            disabled={shownPage <= 1 || state.kind === "loading"}
            onClick={() => setPage(Math.max(1, shownPage - 1))}
          >
            السابق
          </button>
          <button
            type="button"
            className="btn sm ghost"
            disabled={shownPage >= totalPages || state.kind === "loading"}
            onClick={() => setPage(Math.min(totalPages, shownPage + 1))}
          >
            التالي
          </button>
          <span className="t-meta" style={{ marginRight: "auto" }}>
            الشرايح بتتقاطع — «محتاج استكمال» ممكن يكون نشط كمان، فمجموعها مش «الكل».
          </span>
        </div>
      ) : null}
    </Shell>
  );
}


/**
 * موعد رجوع الطالب المتوقف.
 *
 * ⛔ **«مش معروف» مش زي «فاضي»:** الفاضي معناه حد نسي يملاه، و«مش معروف»
 *    قرار متسجّل ومعاه **موعد متابعة إجباري** — فالشاشة بتفرّق بينهم.
 */
function ReturnCell({ plan }: { plan: ReturnPlan | null }) {
  if (!plan) return <span className="t-meta">—</span>;
  if (plan.end_date) return <DateAr v={plan.end_date} />;
  if (plan.end_unknown) {
    return (
      <span className="t-meta">
        مش معروف
        {plan.next_check_at ? (
          <>
            {" · متابعة "}
            <DateAr v={plan.next_check_at} />
          </>
        ) : null}
      </span>
    );
  }
  // ⚠️ توقف مفتوح بلا نهاية ولا «مش معروف» = بند ناقص، مش تفصيلة
  return <span className="missing">محتاج تحديد</span>;
}
